import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

export const Button = forwardRef(({
    className = '',
    variant = 'primary',
    size = 'md',
    isLoading,
    leftIcon,
    rightIcon,
    children,
    ...props
}, ref) => {
    const baseStyles = 'inline-flex items-center justify-center rounded-full font-bold transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-space-blue disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden';

    const variants = {
        primary: 'bg-cyan-500 text-white hover:bg-cyan-400 focus:ring-cyan-500 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] border border-cyan-400/20',
        secondary: 'bg-slate-800 text-white hover:bg-slate-700 focus:ring-slate-500 shadow-lg border border-slate-700',
        outline: 'border-2 border-cyan-500/50 text-cyan-400 hover:border-cyan-400 hover:text-cyan-300 bg-transparent hover:bg-cyan-500/10',
        ghost: 'text-slate-400 hover:bg-slate-800 hover:text-white',
        white: 'bg-white text-space-blue hover:bg-slate-100 shadow-[0_0_20px_rgba(255,255,255,0.3)]',
        cyan: 'bg-cyan-500 text-white hover:bg-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.4)]'
    };

    const sizes = {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-6 text-base',
        lg: 'h-14 px-8 text-lg'
    };

    return (
        <motion.button
            ref={ref}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95, y: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
            disabled={isLoading || props.disabled}
            {...props}
        >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {!isLoading && leftIcon && <span className="mr-2 relative z-10">{leftIcon}</span>}
            <span className="relative z-10">{children}</span>
            {!isLoading && rightIcon && <span className="ml-2 relative z-10">{rightIcon}</span>}

            {/* Shine effect on hover */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full skew-x-12"
                initial={{ x: '-100%' }}
                whileHover={{ x: '200%' }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
            />
        </motion.button>
    );
});

Button.displayName = 'Button';
